import { useMemo } from 'react'

import { jwtDecode } from 'jwt-decode'

import type { Jwt } from '@app/models'
import { hasRole } from '@app/store/user'

import { useAuth } from '../useAuth'
import { useUser } from './useUser'

export const useHasRole = (role: string) => {
  const userState = useUser()

  return useMemo(() => hasRole(userState)(role), [role, userState])
}

export const useIsStaff = () => useHasRole('Staff')
export const useIsStaffAdmin = () => useHasRole('StaffAdmin')

const getOriginalRolesFromToken = (token: string | null | undefined): string[] => {
  try {
    const jwt = token ? jwtDecode<Jwt>(token) : null

    return (
      jwt?.original_roles
        ?.split(',')
        .map((role) => role.trim())
        .filter(Boolean) ?? []
    )
  } catch {
    return []
  }
}

export const hasOriginalStaffRoleWithoutAdminFromToken = (
  token: string | null | undefined
): boolean => {
  const roles = getOriginalRolesFromToken(token)

  return roles.includes('Staff') && !roles.includes('StaffAdmin')
}
export const useIsStaffWithoutStaffAdmin = () => {
  const isStaff = useIsStaff()
  const isStaffAdmin = useIsStaffAdmin()
  const authState = useAuth()

  return useMemo(() => {
    const hasCurrentStaffOnlyRole = isStaff && !isStaffAdmin
    const hasOriginalStaffOnlyRole =
      hasOriginalStaffRoleWithoutAdminFromToken(authState.tokenData.token)

    return hasCurrentStaffOnlyRole || hasOriginalStaffOnlyRole
  }, [authState.tokenData.token, isStaff, isStaffAdmin])
}
